import api from './api';

export interface AvatarResponse {
  success: boolean;
  message?: string;
  avatarUrl?: string;
  hasAvatar?: boolean;
  filename?: string;
}

export interface UploadAvatarResponse extends AvatarResponse {
  avatarUrl: string;
  filename: string;
}

class AvatarService {

  /**
   * 上传当前用户头像
   */
  async uploadAvatar(file: File): Promise<UploadAvatarResponse> {
    // 先验证文件
    const validation = this.validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.message);
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/avatars/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * 删除当前用户头像
   */
  async deleteAvatar(): Promise<AvatarResponse> {
    const response = await api.delete('/avatars/delete');
    return response.data;
  }

  /**
   * 获取当前用户头像
   */
  async getMyAvatar(): Promise<AvatarResponse> {
    const response = await api.get('/avatars/me');
    return response.data;
  }

  /**
   * 获取指定用户头像
   */
  async getUserAvatar(userId: number): Promise<AvatarResponse> {
    const response = await api.get(`/avatars/user/${userId}`);
    return response.data;
  }

  /**
   * 管理员上传指定用户头像
   */
  async adminUploadAvatar(userId: number, file: File): Promise<UploadAvatarResponse> {
    const validation = this.validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.message);
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/avatars/admin/upload/${userId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * 管理员删除指定用户头像
   */
  async adminDeleteAvatar(userId: number): Promise<AvatarResponse> {
    const response = await api.delete(`/avatars/admin/delete/${userId}`);
    return response.data;
  }

  /**
   * 验证文件是否为有效的图片文件
   */
  validateImageFile(file: File): { valid: boolean; message?: string } {
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      return { valid: false, message: '只能上传图片文件' };
    }

    // 检查文件大小 (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, message: '图片大小不能超过 5MB' };
    }

    // 检查文件扩展名
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const fileName = file.name.toLowerCase();
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1);

    if (!allowedExtensions.includes(extension)) {
      return { valid: false, message: '只支持 JPG、PNG、GIF、WebP 格式的图片' };
    }

    return { valid: true };
  }

  /**
   * 生成默认头像URL（基于用户名首字母）
   */
  generateDefaultAvatarUrl(username: string): string {
    const firstLetter = username.charAt(0).toUpperCase();
    // 使用多个头像生成服务作为备选
    const services = [
      `https://ui-avatars.com/api/?name=${firstLetter}&background=1890ff&color=fff&size=200`,
      `https://avatar.vercel.sh/${encodeURIComponent(username)}?size=200`,
      `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(username)}&size=200`
    ];

    // 根据用户名选择一个服务，确保同一用户总是使用相同的默认头像
    const index = username.charCodeAt(0) % services.length;
    return services[index];
  }

  /**
   * 处理头像URL，确保使用正确的完整URL
   */
  getFullAvatarUrl(avatar: string | null | undefined): string | null {
    if (!avatar) return null;

    // 如果已经是完整URL（外部图片服务），直接返回
    if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
      return avatar;
    }

    // 兼容旧的本地文件名格式，返回null让其使用默认头像
    return null;
  }

  /**
   * 获取头像显示URL，如果没有头像则返回默认头像
   */
  getDisplayAvatarUrl(avatarUrl: string | null | undefined, username?: string): string {
    // 首先尝试获取完整的头像URL
    const fullAvatarUrl = this.getFullAvatarUrl(avatarUrl);
    if (fullAvatarUrl) {
      return fullAvatarUrl;
    }

    if (username) {
      return this.generateDefaultAvatarUrl(username);
    }

    // 返回一个通用的默认头像
    return 'https://ui-avatars.com/api/?name=U&background=1890ff&color=fff&size=200';
  }

  /**
   * 网页版特有功能：压缩图片
   */
  async compressImage(file: File, maxWidth = 800, quality = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // 计算新尺寸
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        // 绘制压缩后的图片
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('图片压缩失败'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * 网页版特有功能：裁剪图片为正方形
   */
  async cropToSquare(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;

        // 计算裁剪位置（居中）
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;

        ctx?.drawImage(img, sx, sy, size, size, 0, 0, size, size);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const croppedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(croppedFile);
            } else {
              reject(new Error('图片裁剪失败'));
            }
          },
          file.type,
          0.9
        );
      };

      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * 网页版特有功能：预处理头像（压缩+裁剪）
   */
  async preprocessAvatar(file: File): Promise<File> {
    try {
      // 先裁剪为正方形
      const croppedFile = await this.cropToSquare(file);

      // 再压缩
      const compressedFile = await this.compressImage(croppedFile, 400, 0.8);

      return compressedFile;
    } catch (error) {
      console.warn('头像预处理失败，使用原文件:', error);
      return file;
    }
  }

  /**
   * 网页版特有功能：批量上传头像（用于导入）
   */
  async batchUploadAvatars(files: File[]): Promise<UploadAvatarResponse[]> {
    const results: UploadAvatarResponse[] = [];

    for (const file of files) {
      try {
        const result = await this.uploadAvatar(file);
        results.push(result);
      } catch (error) {
        console.error('批量上传头像失败:', file.name, error);
        results.push({
          success: false,
          message: `上传 ${file.name} 失败: ${error}`,
          avatarUrl: '',
          filename: ''
        });
      }
    }

    return results;
  }

  /**
   * 网页版特有功能：检查头像是否可访问
   */
  async checkAvatarAccessibility(avatarUrl: string): Promise<boolean> {
    try {
      const response = await fetch(avatarUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('检查头像可访问性失败:', error);
      return false;
    }
  }
}

const avatarService = new AvatarService();
export default avatarService;
